import { useState } from 'react';
import { Box, Grid } from '@mui/material';
import { AppTextField } from '../commons/AppTextField';
import SimpleButton from '../commons/SimpleButton';
import { useNavigate } from 'react-router';
import { makeStyles } from '@mui/styles';
import { COLORS, IAppTheme } from '../../utils/AppTheme';
import { appNavigate, AppRoutes } from '../../utils/AppNavigation';
import CustomAuthService from '../../external/fox-typescript/services/CustomAuthService';
import AuthService from '../../external/fox-typescript/services/AuthService';
import { SxStyles } from '../../model/utils/SxStyles';
import { RequestStatus } from '../../utils/RequestStatus';
import AlertService from '../../services/AlertService';
import { AppTypography } from '../Typography/AppTypography';
import { PDLTypography } from '../Typography/PDLTypography';
import { useForm } from '../../hooks/useForm';
import UserService from '../../services/UserService';
import { WelcomeToPdlClub } from '../commons/WelcomeToPdlClub';
import { FooterPdlClub } from '../commons/FooterPdlClub';
import TopNavBar from '../core/LandingPageNavBar';
import { useLocation } from 'react-router-dom';
import { UserAdditionalInfoCheck } from '../../model/UserAdditionalInfoCheck';
import { SignInOtherUserForm } from '../../model/SignInOtherUserForm';
import PdlAuthService from '../../services/PdlAuthService';

interface Props {
  setIsLogged: (value: boolean) => void;
}

export default function SignInAsOther({ setIsLogged }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();

  const [form, , handleCustomChange, formIsValid] = useForm<SignInOtherUserForm>({
    email: '',
    secret: '',
  });
  const [requestStatus, setRequestStatus] = useState(RequestStatus.SUCCESS);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.keyCode == 13 && e.shiftKey == false) {
      e.preventDefault();
      if (formIsValid()) {
        handleSignIn();
      }
    }
  };

  const handleSignIn = async () => {
    try {
      setRequestStatus(RequestStatus.LOADING);

      const result = await PdlAuthService.validateLoginOtherUser(form);

      const { token, expiresAt, userExternalId } = result.getContent();

      AuthService.storeToken(token, expiresAt);
      AuthService.storeExternalId(userExternalId || '');

      setIsLogged(true);

      setRequestStatus(RequestStatus.SUCCESS);
      const userAdditionalInfo = (await UserService.checkUserAdditionalInfo()).getContent();
      redirection(userAdditionalInfo);

    } catch (error: any) {
      if (!error || !error.response) {
        AlertService.showSnackCustomError('Internal Server Error');
      } else if (error?.response?.status === 401) {
        AlertService.showSnackCustomError('The email or secret does not match.');
      } else if (error.response.status === 500) {
        AlertService.showSnackCustomError('User have not activate the account.');
      } else {
        AlertService.showSnackCustomError(error.response.data.message);
      }

      setRequestStatus(RequestStatus.ERROR);
    }
  };

  const redirection = (userAdditionalInfo: UserAdditionalInfoCheck) => {
    const state = location?.state as any;

    // If the user is trying to access a specific path, we navigate to that path after the login
    if (state?.previousPath) {
      appNavigate(navigate, state.previousPath, null);
    }

    // If the user hasn't completed the additional information, we navigate to that view to complete it
    if (!userAdditionalInfo.complete) {
      appNavigate(navigate, AppRoutes.COMPLETE_ADDITIONAL_INFORMATION, null, {
        state: { userAdditionalInfo },
      });
    }
  };

  return (
    <form>
      <Box sx={{ backgroundColor: COLORS.black_000000 }}>
        <TopNavBar />
        <WelcomeToPdlClub />
        <Grid container spacing={8} columns={1} sx={muiStyle.gridHeader}>
          <Grid item xs={12} sx={muiStyle.gridHeader}>
            <AppTypography type={PDLTypography.largeHeading} customClasses={classes.title}>
              Login as other user
            </AppTypography>
          </Grid>
          <Grid item xs={12} sx={{ mx: 4 }}>
            <Grid sx={muiStyle.gridBody}>
              <AppTextField
                label={'Email'}
                name={'email'}
                value={form.email}
                onInputChange={handleCustomChange}
                inputProps={{ sx: muiStyle.input, type: 'email' }}
                labelProps={{ customClasses: classes.label, type: PDLTypography.smallHeading }}
                restrict={'email'}
                realTimeValidation
                required
                onKeyPress={(e) => onKeyDown(e)}
              />

              <AppTextField
                label={'Secret'}
                name={'secret'}
                value={form.secret}
                onInputChange={handleCustomChange}
                inputProps={{ sx: muiStyle.input, type: 'password' }}
                labelProps={{ customClasses: classes.label, type: PDLTypography.smallHeading }}
                realTimeValidation
                required
                minLength={4}
                onKeyPress={(e) => onKeyDown(e)}
              />
              <SimpleButton
                sx={{ marginBottom: '3em', marginTop: '2em', width: '37vh' }}
                children={'Sign In as Other'}
                onClick={handleSignIn}
                type={'submit'}
                disabled={requestStatus === RequestStatus.LOADING || !formIsValid()}
                isLoading={requestStatus === RequestStatus.LOADING}
              />
            </Grid>
          </Grid>
        </Grid>
        <FooterPdlClub dense />
      </Box>
    </form>
  );
}

const useStyles = makeStyles((theme: IAppTheme) => ({
  title: {
    color: COLORS.white_FFFFFF,
  },
  label: {
    color: COLORS.white_FFFFFF,
    marginTop: '1.5em',
    marginBottom: '0.5em',
    fontSize: '.813em',
  },
}));

const muiStyle: SxStyles = {
  gridHeader: {
    marginTop: '0em',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: COLORS.black_000000,
  },
  gridBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  input: {
    '.MuiInputBase-input': {
      backgroundColor: COLORS.white_FFFFFF,
      width: '34vh',
      height: '3vh',
    },
  },
};
